/**
 * Canonical site constants — the single source of truth for NAP (name,
 * address, phone), brand identity, and founder E-E-A-T data. Mirror these
 * values into Google Business Profile and other directories.
 *
 * Plain module (no Sanity dependency) so it is importable from server
 * components, client components, sitemap/robots, and schema builders with
 * zero await. `lib/getSiteSettings.ts` should be reconciled against this.
 */

export const SITE = {
  name: 'Refinity.io',
  legalName: 'Refinity LLC',
  url: 'https://www.refinity.io',
  /** Absolute URL — required by schema.org ImageObject / logo. */
  logo: 'https://www.refinity.io/images/logos/Refinity-IO-Header-Logo-white-transparent-2.png',
  description:
    'Refinity is a marketing agency for growing businesses, building content strategy, SEO, paid media, measurement and marketing automation into practical growth systems.',
  /** Brand profiles used for Organization.sameAs (entity disambiguation). */
  sameAs: [
    'https://www.linkedin.com/company/refinity-io',
    'https://www.instagram.com/refinity.io',
    'https://linktr.ee/refinity.io',
  ],
  contact: {
    email: 'contact@refinity.io',
    phone: '+1-678-430-8725',
  },
  /** Primary conversion CTA — the Calendly "Book a Call" link used site-wide. */
  bookingUrl: 'https://calendly.com/jon-solomon-refinity',
  /** Postal address (matches getSiteSettings fallback). */
  address: {
    streetAddress: '2055 Barrett Lakes Blvd NW',
    addressLocality: 'Kennesaw',
    addressRegion: 'GA',
    postalCode: '30144',
    addressCountry: 'US',
  },
  geo: { latitude: 34.0234, longitude: -84.6155 },
  /** Areas the business serves — Atlanta metro is a secondary signal only. */
  areaServed: ['Atlanta', 'Georgia', 'United States'],
  /** Founder identity for Person schema + article authorship (E-E-A-T). */
  founder: {
    name: 'Jonathan Solomon',
    alternateName: 'JSol',
    jobTitle: 'Founder & CEO',
    slug: 'jonathan-solomon',
    url: 'https://www.refinity.io/team/jonathan-solomon',
    sameAs: [
      'https://linkedin.com/in/jsolmktg',
      'https://x.com/jsolmktg',
      'https://instagram.com/jsolmktg',
      'https://www.youtube.com/@jsolmktg',
      'https://tiktok.com/@JSolMKTG',
    ],
    knowsAbout: [
      'Marketing Automation',
      'Demand Generation',
      'Search Engine Optimization',
      'Paid Media',
      'Content Strategy',
      'Growth Marketing',
    ],
  },
} as const

/** Stable schema.org @id anchors so JSON-LD nodes cross-reference into one graph. */
export const SCHEMA_IDS = {
  organization: `${SITE.url}/#organization`,
  website: `${SITE.url}/#website`,
  founder: `${SITE.url}/team/${SITE.founder.slug}#person`,
} as const

/** Resolve a site-relative path to an absolute URL. */
export function absoluteUrl(path = '/'): string {
  if (/^https?:\/\//.test(path)) return path
  return `${SITE.url}${path.startsWith('/') ? path : `/${path}`}`
}
